import { ref, computed, unref, watchEffect } from 'vue'
import http from '@/utils/http'

/**
 * 공휴일 조회 — 연도 단위 세션 캐시.
 *
 * 이전에는 MonthView / TaskMonthView / CustomTimeGrid / CustomMonthGrid 가 각자
 * /api/holidays 를 불렀고, 그중 셋은 currentDate 가 바뀔 때마다 다시 불렀다.
 * 월을 몇 번 넘기면 같은 연도를 열 번 넘게 조회하게 된다.
 *
 * 모듈 레벨 Map 에 Promise 를 담아두므로 같은 연도는 세션당 한 번만 나간다.
 * (Promise 를 캐시해야 동시에 여러 컴포넌트가 마운트돼도 요청이 하나로 합쳐진다)
 */
const cache = new Map() // year -> Promise<Holiday[]>

function fetchYear(year) {
  if (!cache.has(year)) {
    cache.set(
      year,
      http
        .get('/api/holidays', { params: { year } })
        .then((res) => res.data.holidays || [])
        .catch(() => {
          // 실패한 연도는 캐시에서 지워 다음 기회에 재시도할 수 있게 한다
          cache.delete(year)
          return []
        })
    )
  }
  return cache.get(year)
}

/**
 * @param {import('vue').Ref<number|number[]>|number|number[]} yearRef 조회할 연도.
 *   배열이면 여러 해를 합친다 — 화면이 연도 경계를 걸칠 때(1월 그리드의 12월 말,
 *   12월 마지막 주의 1월 1일) 한 해만 받으면 그 칸의 공휴일이 빠진다.
 */
export function useHolidays(yearRef) {
  const holidays = ref([])

  const byDate = computed(() => {
    const map = {}
    for (const h of holidays.value) map[h.date] = h.name
    return map
  })

  watchEffect(async () => {
    const years = [...new Set([].concat(unref(yearRef)).map(Number).filter(Boolean))]
    if (!years.length) return
    const lists = await Promise.all(years.map(fetchYear))
    holidays.value = lists.flat()
  })

  return {
    holidays,
    byDate,
    /** @param {string} ymd 'YYYY-MM-DD' */
    isHoliday: (ymd) => !!byDate.value[ymd],
    holidayName: (ymd) => byDate.value[ymd] || ''
  }
}
